import { useEffect, useState, type FormEvent } from 'react'
import { Link, Navigate, useNavigate, useSearchParams } from 'react-router-dom'
import { Button, InlineError, PhoneInput } from '../../components/ui'
import { useAppDispatch } from '../../store/hooks'
import { signedIn } from '../../features/auth/authSlice'
import { useSignInMutation } from '../../services/authApi'
import { executeRecaptcha, loadRecaptcha, recaptchaAvailable } from '../../services/recaptcha'
import { AuthSplit } from './AuthCard'
import { PasswordInput } from './PasswordInput'
import { isPlausibleMobile, toE164 } from '../../utils/phone'
import { CAPTCHA_AFTER_FAILURES, clearFailures, needsCaptcha, recordFailure } from './loginAttempts'
import { safeNext } from './returnTo'
import { useSession } from './useSession'

/** H3 — `/{slug}/signin`. */
export function SignInPage() {
  const { slug, isAuthenticated } = useSession()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const next = safeNext(params.get('next'), slug)

  const [mobile, setMobile] = useState('')
  const [password, setPassword] = useState('')
  const [touched, setTouched] = useState(false)
  const [signIn, { isLoading, error }] = useSignInMutation()

  // TC-LOGIN-004: past this many failures every attempt carries a reCAPTCHA
  // Enterprise token, so the backend can tell a forgetful customer from a script.
  const mobileE164 = toE164(mobile)
  const [guarded, setGuarded] = useState(() => needsCaptcha(slug, mobileE164))

  // Re-check when the customer types a different number: the count is kept per
  // number as well as per device.
  useEffect(() => {
    if (!guarded && needsCaptcha(slug, mobileE164)) setGuarded(true)
  }, [guarded, slug, mobileE164])

  // Fetch the script as soon as the guard turns on rather than on submit, so the
  // 6th attempt is not slower than the first five.
  useEffect(() => {
    if (guarded && recaptchaAvailable) void loadRecaptcha().catch(() => undefined)
  }, [guarded])

  const mobileError = touched && !isPlausibleMobile(mobile) ? 'Enter your 10-digit phone number, for example 415 555 0132.' : undefined
  const passwordError = touched && password.length === 0 ? 'Enter your password.' : undefined

  if (isAuthenticated) return <Navigate to={next} replace />

  const submit = async (event: FormEvent) => {
    event.preventDefault()
    setTouched(true)
    if (!isPlausibleMobile(mobile) || !password) return

    // A token that cannot be minted (blocked script, offline) is sent as null: the
    // backend decides what a missing token is worth, the form never locks anyone out.
    const recaptchaToken = guarded ? await executeRecaptcha('LOGIN') : null

    const result = await signIn({ slug, mobile: mobileE164, password, recaptchaToken })
    if (!('data' in result) || !result.data) {
      const failures = recordFailure(slug, mobileE164)
      if (failures >= CAPTCHA_AFTER_FAILURES) setGuarded(true)
      return
    }

    clearFailures(slug, mobileE164)

    dispatch(
      signedIn({
        slug,
        token: result.data.token,
        expiresIn: result.data.expires_in ?? undefined,
        customer: result.data.customer,
      }),
    )
    navigate(next, { replace: true })
  }

  return (
    <AuthSplit
      title="Welcome back"
      subtitle="Sign in to manage your appointments and see your visit history."
      footer={
        <>
          Don&apos;t have an account?{' '}
          <Link
            to={`/${slug}/signup?next=${encodeURIComponent(next)}`}
            className="ml-1 font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
          >
            Sign up
          </Link>
        </>
      }
    >
      <form onSubmit={submit} noValidate className="space-y-8">
        {error && <InlineError error={error} />}

        <div className="space-y-6">
          <PhoneInput
            label="Phone number"
            value={mobile}
            onChange={setMobile}
            error={mobileError}
            hint="Use the phone number you gave the salon."
            required
          />

          <div className="space-y-2">
            <PasswordInput
              label="Password"
              value={password}
              onChange={setPassword}
              error={passwordError}
              required
            />
            <div className="text-right">
              <Link
                to={`/${slug}/reset`}
                className="font-label-caps text-label-caps uppercase text-primary transition-colors hover:text-primary-container"
              >
                Forgot password?
              </Link>
            </div>
          </div>
        </div>

        {guarded && (
          <p
            role="status"
            className="flex items-start gap-2 rounded-lg border border-primary/10 bg-surface-container-low px-4 py-3 font-body-md text-sm text-on-surface-variant"
          >
            <span aria-hidden="true" className="material-symbols-outlined mt-0.5 text-[18px] text-primary">
              verified_user
            </span>
            <span>
              After several failed attempts we check this device for automated sign-ins. Nothing to do —
              it runs in the background. If you have forgotten your password,{' '}
              <Link to={`/${slug}/reset`} className="text-primary underline">
                reset it
              </Link>
              .
            </span>
          </p>
        )}

        <Button type="submit" fullWidth size="lg" loading={isLoading}>
          Sign in
          <span aria-hidden="true" className="material-symbols-outlined text-[18px]">
            arrow_forward
          </span>
        </Button>

        {/* Google requires this wording wherever the reCAPTCHA badge is hidden. */}
        {guarded && recaptchaAvailable && (
          <p className="text-center font-body-md text-xs text-outline">
            Protected by reCAPTCHA — Google&apos;s{' '}
            <a
              href="https://policies.google.com/privacy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Privacy Policy
            </a>{' '}
            and{' '}
            <a
              href="https://policies.google.com/terms"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
            >
              Terms of Service
            </a>{' '}
            apply.
          </p>
        )}
      </form>
    </AuthSplit>
  )
}
